/**
 * Wraps a Lucide icon or custom SVG with consistent sizing and layout.
 * Architectural Context: Pure UI atom. Supports forwardRef for tooltips and Framer Motion.
 * Named IconWrapper to avoid .gitignore conflict with macOS Icon rule.
 *
 * @param children - Icon element (e.g. <AlertCircle />)
 * @param size - Wrapper size; inner svg constrained via [&>svg]
 * @param color - Icon color from design tokens
 * @param strokeWidth - Lucide SVG stroke width (1, 1.5, 2, 2.5)
 * @param rotation - Rotation in degrees (0, 90, 180, 270)
 * @param ariaLabel - Accessible label for meaningful icons
 * @param ariaHidden - Hide from assistive tech when decorative
 * @default size 'md'
 * @default color 'inherit'
 *
 * @example
 * <IconWrapper size="md" color="error"><AlertCircle /></IconWrapper>
 * <IconWrapper size="sm" ariaLabel="Close" rotation={90}><ChevronRight /></IconWrapper>
 */

import React from 'react';
import { cn } from '@/utils/cn';
import {
  colorStyles,
  rotationStyles,
  sizeStyles,
  strokeWidthStyles,
} from './IconWrapper.styles';
import type { IconWrapperProps } from './IconWrapper.types';

export const IconWrapper = React.forwardRef<HTMLSpanElement, IconWrapperProps>(
  (
    {
      children,
      size = 'md',
      color = 'inherit',
      strokeWidth,
      rotation = 0,
      ariaLabel,
      ariaHidden,
      className,
      ...props
    },
    ref
  ) => {
    const isMeaningful = Boolean(ariaLabel);
    const ariaProps = isMeaningful
      ? { role: 'img' as const, 'aria-label': ariaLabel }
      : ariaHidden === true
        ? { 'aria-hidden': true }
        : {};

    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex shrink-0 items-center justify-center',
          sizeStyles[size],
          colorStyles[color],
          '[&>svg]:h-full [&>svg]:w-full',
          rotation !== 0 && rotationStyles[rotation],
          strokeWidth != null && strokeWidthStyles[strokeWidth],
          className
        )}
        {...ariaProps}
        {...props}
      >
        {children}
      </span>
    );
  }
);

IconWrapper.displayName = 'IconWrapper';
